import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ScrollToTop from '../../components/ScrollTop';
import PageBanner from '../../components/PageBanner';
import { FaChevronDown, FaSearch, FaFilePdf, FaUserGraduate, FaUsers } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import { FiCheckCircle } from 'react-icons/fi';
import { IoArrowForwardOutline } from 'react-icons/io5';
import { getExamination } from '../../services/data.service';
import { mediaUrl } from '../../services/assetUrl';

// Resolve a circular to its document href: prefer the explicit url, else fall
// back to the /view/examination/circulars/<file> S3 path (where the PDFs live).
const circularHref = (c) => {
    if (c.url) return mediaUrl(c.url);
    if (c.file) return mediaUrl(`/view/examination/circulars/${c.file}`);
    return '#';
};

const decodeEntities = (s) => (s || '').replace(/&amp;/g, '&').replace(/&#39;/g, "'");

// Circulars are stored without a date, but every title carries its exam period
// (e.g. "Dec-2025 Jan-2026", "July 2026", "May-June 2026"). Extract the newest
// exam-period date from the title so the list can be shown newest-first.
const sortCircularsNewestFirst = (list) =>
  [...list].reverse();

// Consistent scroll-reveal used across the site
const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};
const reveal = {
    variants: fadeUp,
    initial: 'hidden',
    whileInView: 'show',
    viewport: { once: true, amount: 0.12 }
};

const Examination = () => {
    const [model, setModel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isMessageExpanded, setIsMessageExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(8);
    const [selectedBatchIndex, setSelectedBatchIndex] = useState(null);

    useEffect(() => {
        document.title = "Examination | NCET";
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await getExamination();
                if (active) setModel(res?.data || null);
            } catch (e) {
                if (active) setModel(null);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        setVisibleCount(8);
    }, [searchQuery]);

    const hero = model?.hero || {};
    const coe = model?.coeProfile || {};
    const coeCard = coe.card || {};
    const coeMessage = coe.message || {};
    const circularsSection = model?.circularsSection || {};
    const portalsSection = model?.portalsSection || {};
    const achievementsSection = model?.achievementsSection || {};
    const batchesSection = model?.batchesSection || {};

    const circulars = model?.circulars || [];
    const portals = model?.portals || [];
    const achievementsData = model?.achievements || [];
    const graduatingBatches = model?.graduatingBatches || [];

    // Newest exam period first (undated items fall to the bottom).
    const sortedCirculars = sortCircularsNewestFirst(circulars);
    const filteredCirculars = sortedCirculars.filter(c =>
        (c.title || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    const currentCirculars = filteredCirculars.slice(0, visibleCount);

    return (
        <div className="examination-page">
            <Header />

            <PageBanner
                title={hero.title || 'Excellence in Evaluation'}
                eyebrow={hero.eyebrow || 'EXAMINATION'}
                subtitle={hero.subtitle}
                image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
            />

            {loading ? (
                <div className="container-custom examination-page__content" style={{ padding: '6rem 0', textAlign: 'center', color: '#53545b' }}>
                    Loading examination information…
                </div>
            ) : !model ? (
                <div className="container-custom examination-page__content" style={{ padding: '6rem 0', textAlign: 'center', color: '#53545b' }}>
                    Examination information is currently unavailable. Please check back later.
                </div>
            ) : (
            <div className="container-custom examination-page__content">

                {/* COE Profile Section */}
                <motion.section className="coe-profile" {...reveal}>
                    <div className="section-header">
                        <span className="badge-text">{coe.badge}</span>
                        <h2>{coe.heading}</h2>
                        <p>{coe.description}</p>
                    </div>

                    <div className="coe-showcase">
                        <div className="coe-desk">
                            <div className="coe-desk__top">
                                <span className="desk-badge">{coe.deskBadge}</span>
                                <h4>{coe.deskHeading}</h4>
                            </div>

                            <div className={`coe-message-body ${isMessageExpanded ? 'expanded' : ''}`}>
                                {coeMessage.lead && <p className="lead">{coeMessage.lead}</p>}
                                {(coeMessage.paragraphs || []).map((para, i) => (
                                    <p key={i}>{para}</p>
                                ))}
                            </div>

                            <button
                                className={`coe-read-toggle ${isMessageExpanded ? 'expanded' : ''}`}
                                onClick={() => setIsMessageExpanded(!isMessageExpanded)}
                            >
                                <span>{isMessageExpanded ? 'Show Less' : 'Read Full Message'}</span>
                                <FaChevronDown size={14} />
                            </button>

                            <div className="coe-service-grid">
                                {(coe.services || []).map((service, i) => (
                                    <article key={i}>
                                        <span className={`service-icon ${service.color || 'orange'}`}><FiCheckCircle size={20} /></span>
                                        <div>
                                            <h5>{service.title}</h5>
                                            <p>{service.desc}</p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>

                        <aside className="coe-identity-card">
                            <div className="coe-photo-wrap">
                                <img src={mediaUrl(coeCard.photo)} alt={coeCard.name} />
                                {coeCard.qualification && <span className="coe-qualification">{coeCard.qualification}</span>}
                            </div>

                            <div className="coe-meta">
                                <h3>{coeCard.name}</h3>
                                <p className="role">{coeCard.role}</p>
                                <p className="unit">{coeCard.unit}</p>
                            </div>

                            <div className="coe-contact-list">
                                {(coeCard.emails || []).map((email, i) => (
                                    <a key={i} href={`mailto:${email.value}`}>
                                        <span className="icon-box"><HiOutlineMail size={18} /></span>
                                        <span className="label-wrap">
                                            <span className="label">{email.label}</span>
                                            <span className="value">{email.value}</span>
                                        </span>
                                    </a>
                                ))}
                            </div>

                            <div className="coe-pillars">
                                {(coeCard.pillars || []).map((pillar, i) => (
                                    <span key={i}>{pillar}</span>
                                ))}
                            </div>
                        </aside>
                    </div>
                </motion.section>

                {/* Circulars Section */}
                <motion.section className="exam-circulars" {...reveal}>
                    <div className="section-header">
                        <span className="badge-text">{circularsSection.badge}</span>
                        <h2>{circularsSection.heading}</h2>
                        <p>{circularsSection.description}</p>
                    </div>

                    <div className="exam-circulars__panel">
                        <div className="exam-circulars__toolbar">
                            <label className="exam-circulars__search">
                                <FaSearch size={16} />
                                <input
                                    type="text"
                                    placeholder="Search circulars by title or keyword..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </label>
                            <div className="exam-circulars__count">
                                <FaFilePdf size={16} />
                                <span><b>{filteredCirculars.length}</b> Circulars Total</span>
                            </div>
                        </div>

                        <div className="exam-circulars__grid">
                            {currentCirculars.length > 0 ? currentCirculars.map((circular, index) => (
                                <a key={circular._id || index} href={circularHref(circular)} target="_blank" rel="noreferrer" className="exam-circular-card">
                                    <div className="exam-circular-card__head">
                                        <div className="exam-circular-card__icon">
                                            <FaFilePdf size={18} />
                                        </div>
                                    </div>
                                    <h4 className="exam-circular-card__title">{decodeEntities(circular.title)}</h4>
                                    <div className="exam-circular-card__cta">
                                        View Document
                                        <IoArrowForwardOutline size={16} />
                                    </div>
                                </a>
                            )) : (
                                <div className="exam-circulars__empty">No circulars found matching your search.</div>
                            )}
                        </div>

                        {visibleCount < filteredCirculars.length && (
                            <div className="exam-circulars__more-wrap">
                                <button type="button" className="exam-circulars__more-btn" onClick={() => setVisibleCount(prev => prev + 8)}>
                                    Show More Circulars
                                    <FaChevronDown size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                </motion.section>

                {/* Portals Section */}
                <motion.section className="exam-portals" {...reveal}>
                    <div className="section-header">
                        <span className="badge-text">{portalsSection.badge}</span>
                        <h2>{portalsSection.heading}</h2>
                    </div>

                    <div className="exam-portals__grid">
                        {[...portals].reverse().map((portal, i) => {
                            const isParent = portal.icon === 'users';
                            return (
                                <a key={portal._id || i} href={portal.link} target="_blank" rel="noopener noreferrer" className={`exam-portal ${isParent ? 'exam-portal--parent' : 'exam-portal--student'}`}>
                                    <div className="exam-portal__top">
                                        <span className="exam-portal__icon">{isParent ? <FaUsers size={24} /> : <FaUserGraduate size={24} />}</span>
                                        <span className="exam-portal__tag">{portal.tag}</span>
                                    </div>
                                    <h3 className="exam-portal__title">{portal.title}</h3>
                                    <p className="exam-portal__desc">{portal.desc}</p>
                                    <span className="exam-portal__cta">Access Portal <IoArrowForwardOutline size={18} /></span>
                                </a>
                            );
                        })}
                    </div>
                </motion.section>

                {/* Achievements Section */}
                {achievementsData.length > 0 && (
                <motion.section className="achievements-section" {...reveal}>
                    <div className="section-header">
                        <span className="badge-text">{achievementsSection.badge}</span>
                        <h2>{achievementsSection.heading}</h2>
                        <p>{achievementsSection.description}</p>
                    </div>

                    <Swiper
                        modules={[Pagination, Autoplay]}
                        spaceBetween={30}
                        slidesPerView={1}
                        pagination={{ clickable: true }}
                        autoplay={{ delay: 3000 }}
                        breakpoints={{
                            640: { slidesPerView: 2 },
                            1024: { slidesPerView: 3 },
                            1280: { slidesPerView: 4 }
                        }}
                        className="swiper-container"
                    >
                        {achievementsData.map((item, i) => (
                            <SwiperSlide key={item._id || i}>
                                <div className="achievement-card" onClick={() => item.pdf && window.open(mediaUrl(item.pdf), '_blank')} style={{ cursor: 'pointer' }}>
                                    <div className="img-box">
                                        <img src={mediaUrl(item.img)} alt={item.title} />
                                    </div>
                                    <div className="content">
                                        <h4>{item.title}</h4>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </motion.section>
                )}

                {/* Graduating Batches */}
                {graduatingBatches.length > 0 && (
                <motion.section className="exam-batches" {...reveal}>
                    <div className="section-header">
                        <span className="badge-text">{batchesSection.badge}</span>
                        <h2>{batchesSection.heading}</h2>
                        <p>{batchesSection.description}</p>
                    </div>

                    <div className="exam-batches__grid">
                        {graduatingBatches.map((item, index) => (
                            <div key={item._id || index} className="exam-batch" onClick={() => setSelectedBatchIndex(index)} style={{ cursor: 'pointer' }}>
                                <img src={mediaUrl(item.img)} alt={item.title} loading="lazy" />
                                <div className="exam-batch__overlay"></div>
                                <span className="exam-batch__year">{item.batchLabel}</span>
                                <h3 className="exam-batch__title">{item.title}</h3>
                            </div>
                        ))}
                    </div>
                </motion.section>
                )}

                {/* Lightbox for Graduating Batches */}
                {selectedBatchIndex !== null && graduatingBatches[selectedBatchIndex] && (
                    <div className="exam-batch-lightbox" role="dialog" aria-modal="true" aria-label="Graduating batch photo viewer">
                        <button type="button" className="exam-batch-lightbox__close" onClick={() => setSelectedBatchIndex(null)} aria-label="Close">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>

                        <button type="button" className="exam-batch-lightbox__nav exam-batch-lightbox__nav--prev" onClick={() => setSelectedBatchIndex(prev => prev > 0 ? prev - 1 : graduatingBatches.length - 1)} aria-label="Previous batch">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                        </button>

                        <div className="exam-batch-lightbox__content">
                            <img src={mediaUrl(graduatingBatches[selectedBatchIndex].img)} alt={graduatingBatches[selectedBatchIndex].title} />
                            <h3>{graduatingBatches[selectedBatchIndex].title}</h3>
                            <p>{graduatingBatches[selectedBatchIndex].batchLabel}</p>
                        </div>

                        <button type="button" className="exam-batch-lightbox__nav exam-batch-lightbox__nav--next" onClick={() => setSelectedBatchIndex(prev => prev < graduatingBatches.length - 1 ? prev + 1 : 0)} aria-label="Next batch">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                        </button>
                    </div>
                )}
            </div>
            )}

            <Footer />
            <ScrollToTop />
        </div>
    );
};

export default Examination;
